import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import { formatSize } from './presentations/shareUtils';
import AdminPresentationConsents from './AdminPresentationConsents';
import './presentations/Presentations.css';

const STATUS = {
    checking: 'Проверяет ИИ',
    review: 'Ждёт решения',
    ready: 'Рассылается',
    blocked: 'Заблокирован',
};
const MODES = [
    ['ai', 'ИИ проверяет текст слайдов', 'Подозрительные файлы и файлы с жалобами ждут вашего решения. Остальные рассылаются сразу после проверки.'],
    ['manual', 'Каждый файл — вручную', 'Ни один файл не уйдёт участникам, пока вы его не одобрите. Надёжнее всего, но организаторам придётся ждать.'],
    ['off', 'Только проверка файла', 'Без ИИ: отклоняются файлы с макросами, скриптами и встроенными программами. Жалобы по-прежнему приходят вам.'],
];
const FILTERS = [
    ['attention', 'Требуют внимания'],
    ['active', 'Активные'],
    ['blocked', 'Заблокированные'],
    ['deleted', 'Удалённые'],
];
const EMPTY = {
    attention: 'Нет файлов, ждущих решения.',
    active: 'Сейчас нет загруженных файлов.',
    blocked: 'Заблокированных файлов нет.',
    deleted: 'Удалённых файлов нет. Сюда попадают файлы, которые удалил организатор или у которых закончился срок хранения.',
};

const fmt = (v) => (v ? new Date(v).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function statusLabel(item) {
    if (item.deleted) return item.deleted_label || 'Удалён';
    return STATUS[item.status] || item.status;
}

/** Текст слайдов — то, что читает ИИ. Удобно для PPTX, который браузер не показывает. */
function SlideText({ id }) {
    const [text, setText] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        api.get(`/admin/presentations/${id}/text`)
            .then((res) => setText(res.data.text))
            .catch((err) => setError(getApiErrorMessage(err, 'Не удалось прочитать текст')));
    }, [id]);
    if (error) return <p className="pres-error">{error}</p>;
    if (text === null) return <p className="pres-muted">Читаем…</p>;
    return <pre className="apres-text">{text || 'Текста на слайдах нет — вероятно, слайды сохранены картинками. Откройте файл.'}</pre>;
}

/** Админка: проверка презентаций «Поделиться презентацией» — режим, очередь, жалобы, блокировка. */
function AdminPresentations() {
    const [filter, setFilter] = useState('attention');
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(null);
    const [textFor, setTextFor] = useState(null);
    const [section, setSection] = useState('files'); // files | consents

    const load = useCallback(async () => {
        try {
            const res = await api.get('/admin/presentations', { params: { status: filter } });
            setData(res.data);
            setError('');
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить'));
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const setMode = async (mode) => {
        try {
            await api.put('/admin/presentations/settings', { review_mode: mode });
            setData((prev) => ({ ...prev, review_mode: mode }));
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось сохранить'));
        }
    };

    const decide = async (item, action) => {
        let reason;
        if (action === 'block') {
            reason = window.prompt('Причина блокировки — её увидит организатор в письме:', 'Нарушение правил сервиса');
            if (reason === null) return;
        }
        setBusy(item.id);
        try {
            await api.post(`/admin/presentations/${item.id}/decision`, { action, reason });
            await load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось выполнить'));
        } finally {
            setBusy(null);
        }
    };

    // PDF открываем в новой вкладке по ссылке на 5 минут, PPTX и ODP браузер показать не может — скачиваем
    const openFile = async (item) => {
        const view = item.file_type === 'pdf' ? window.open('', '_blank') : null; // сразу, иначе браузер заблокирует окно
        try {
            if (view) {
                const res = await api.post(`/admin/presentations/${item.id}/view-link`);
                view.location.href = `${api.defaults.baseURL || ''}${res.data.url}`;
                return;
            }
            const res = await api.get(`/admin/presentations/${item.id}/file`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.title || `review-${item.id}.${item.file_type}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            if (view) view.close();
            setError(err.response?.status === 404 ? 'Файл уже удалён с сервера' : 'Не удалось открыть файл');
            load();
        }
    };

    const counts = data?.counts || {};

    return (
        <section className="admin-main-card">
            <h2 className="admin-main-card__title">Презентации</h2>
            <div className="nl-body">
                <div className="apres-sections" role="tablist" aria-label="Раздел">
                    <button type="button" role="tab" aria-selected={section === 'files'} className={section === 'files' ? 'is-active' : ''} onClick={() => setSection('files')}>Файлы</button>
                    <button type="button" role="tab" aria-selected={section === 'consents'} className={section === 'consents' ? 'is-active' : ''} onClick={() => setSection('consents')}>Почты и согласия</button>
                </div>
                {section === 'consents' ? <AdminPresentationConsents /> : (<>
                <p className="pres-muted">
                    Файлы, которые организаторы рассылают участникам. Организатор при загрузке подтверждает, что отвечает за содержимое.
                    Жалоба получателя сразу скрывает файл до вашего решения.
                </p>
                {error && <p className="pres-error" role="alert">{error}</p>}
                {data && (
                    <>
                        <div className="apres-modes" role="radiogroup" aria-label="Режим проверки">
                            {MODES.map(([value, title, hint]) => (
                                <label key={value} className={`apres-mode${data.review_mode === value ? ' is-active' : ''}`}>
                                    <input type="radio" name="apres-mode" checked={data.review_mode === value} onChange={() => setMode(value)} />
                                    <span><b>{title}</b><small>{hint}</small></span>
                                </label>
                            ))}
                        </div>
                        <p className="pres-muted">
                            ИИ для проверки: {data.ai_configured ? 'подключён (вкладка «ИИ-модерация»)' : 'не подключён — файлы проходят без проверки ИИ'}.
                            {' '}Антивирус ClamAV: {data.antivirus ? 'работает' : 'не установлен'}.
                            {' '}ИИ читает только текст слайдов: картинки он не видит.
                        </p>
                        <div className="apres-filter" role="tablist" aria-label="Какие файлы показать">
                            {FILTERS.map(([v, t]) => (
                                <button key={v} type="button" role="tab" aria-selected={filter === v}
                                        className={`btn btn--sm ${filter === v ? 'btn--primary' : 'btn--secondary'}`} onClick={() => setFilter(v)}>
                                    {t}{counts[v] ? ` (${counts[v]})` : ''}
                                </button>
                            ))}
                        </div>
                        {data.items.length === 0 ? (
                            <p className="pres-empty">{EMPTY[filter]}</p>
                        ) : (
                            <ul className="pres-files">
                                {data.items.map((item) => (
                                    <li key={item.id} className={`pres-file apres-item apres-item--${item.deleted ? 'deleted' : item.status}`}>
                                        <div className="pres-file__info">
                                            <b>{item.title}</b>
                                            <span>{item.owner_email} · {item.file_type.toUpperCase()} · {formatSize(item.size)} · загружен {fmt(item.created_at)}</span>
                                            <span className={`apres-status apres-status--${item.deleted ? 'deleted' : item.status}`}>
                                                {statusLabel(item)}{item.deleted ? ` ${fmt(item.deleted_at)}` : ''}
                                            </span>
                                            {item.check_note && <span>Проверка: {item.check_note}</span>}
                                            {item.block_reason && <span>Причина блокировки: {item.block_reason}</span>}
                                            {item.used_in.length > 0 && <span>Подключён: {item.used_in.join(', ')}</span>}
                                            <span>Запросили {item.requests.total} · отправлено {item.requests.sent} · скачали {item.requests.downloads}</span>
                                            {item.complaints.map((c, i) => (
                                                <span key={i} className={c.resolved ? 'apres-complaint apres-complaint--resolved' : 'apres-complaint'}>
                                                    Жалоба {fmt(c.created_at)}: {c.reason}{c.text ? ` — «${c.text}»` : ''}{c.resolved ? ' (рассмотрена)' : ''}
                                                </span>
                                            ))}
                                            <span className="apres-ack">
                                                {item.ack_at ? `Ответственность подтверждена ${fmt(item.ack_at)}, IP ${item.ack_ip || '—'}, текст от ${item.ack_version}` : 'Загружен до появления подтверждения'}
                                            </span>
                                            {textFor === item.id && <SlideText id={item.id} />}
                                        </div>
                                        <div className="apres-actions">
                                            {item.file_available ? (
                                                <>
                                                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => openFile(item)}>
                                                        {item.file_type === 'pdf' ? 'Открыть' : 'Скачать'}
                                                    </button>
                                                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setTextFor(textFor === item.id ? null : item.id)}>
                                                        {textFor === item.id ? 'Скрыть текст' : 'Текст слайдов'}
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="apres-nofile">Файла на сервере нет</span>
                                            )}
                                            {!item.deleted && item.status !== 'ready' && item.status !== 'blocked' && (
                                                <button type="button" className="btn btn--primary btn--sm" disabled={busy === item.id} onClick={() => decide(item, 'approve')}>Разрешить</button>
                                            )}
                                            {!item.deleted && item.status !== 'blocked' && (
                                                <button type="button" className="btn btn--danger btn--sm" disabled={busy === item.id} onClick={() => decide(item, 'block')}>Заблокировать</button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
                </>)}
            </div>
        </section>
    );
}

export default AdminPresentations;
